import { useMemo } from "react";

import { AiFillEdit, AiFillDelete, AiFillEye } from "react-icons/ai";
import { FaCalendarAlt } from "react-icons/fa";
import { MdAddCircleOutline, MdSearch } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import {
  useTable,
  useSortBy,
  useGlobalFilter,
  usePagination,
} from "react-table";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Oval } from "react-loader-spinner";
import { useStore } from "context/GlobalContext";
import { format } from "date-fns/esm";

const CaseLogTable = ({
  data,
  startDate,
  endDate,
  setDateRange,
  loading,
  searchDatabase,
  handleDelete,
}) => {
  const router = useNavigate();
  const { searchText, setSearchText, fullSearch, setFullSearch } = useStore();
  const handleAdd = async () => {
    router("/cases/new-case");
  };

  const handleNavigate = (row, isEdit) => {
    if (isEdit) {
      router(`/cases/edit-case/${row.caseNumber}`, {
        state: { data: row },
      });
      return;
    }

    router(`/cases/${row.caseNumber}`, {
      state: { data: row },
    });
  };

  const columns = useMemo(
    () => [
      {
        Header: "Case Number",
        accessor: "caseNumber",
      },
      {
        Header: "Incident Type",
        accessor: "incidentType",
      },
      {
        Header: "Category",
        accessor: "category",
      },
      {
        Header: "Location",
        accessor: "location",
      },
      {
        Header: "Zone",
        accessor: "zone",
      },
      {
        Header: "Investigator",
        accessor: "investigatorAssigned",
      },

      {
        Header: "PNG",
        accessor: "png",
      },

      {
        Header: "Date",
        accessor: (row) => format(new Date(row.date), "d/M/yyyy"),
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page, // Instead of using 'rows', we'll use page,
    // which has only the rows for the active page
    rows,
    // The rest of these things are super handy, too ;)
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },

    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
    },

    useGlobalFilter,
    useSortBy,
    usePagination
  );

  return (
    <>
      <div className=" relative flex justify-between   flex-col md:flex-row items-center">
        <button
          onClick={() => {
            handleAdd();
          }}
          className="flex ml-1 self-start   items-center justify-center space-x-2 bg-blue-500 hover:bg-blue-500/70 px-3 py-2 mb-2 rounded transition-all duration-300 ease-out shadow-md"
        >
          <span>Add</span> <MdAddCircleOutline className="w-5 h-5" />
        </button>

        <div className="relative h-10 w-full mx-5 xl:ml-[20%] group flex items-center">
          <div className=" absolute  right-10 flex rounded items-center space-x-3 px-3 justify-center bg-secondary">
            <label>Full</label>
            <input
              checked={fullSearch}
              type="checkbox"
              onChange={(e) => setFullSearch(e.target.checked)}
              className="justify-self-center"
            />
          </div>
          <input
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              !fullSearch && setGlobalFilter(e.target.value);
            }}
            placeholder={
              fullSearch ? "Search description with any details" : "Search..."
            }
            className="    bg-transparent outline-none border-b h-10 border-primary w-full  "
          />
          <MdSearch
            onClick={() => {
              if (fullSearch) {
                searchDatabase(searchText);
              }
            }}
            className={`w-8 h-8 absolute right-0 ${
              fullSearch && "hover:text-primary cursor-pointer animate-pulse "
            }`}
          />
        </div>
        <div className="self-end py-2 flex flex-col   px-3 rounded absolute -top-3 md:static">
          <div className="flex space-x-2 items-center">
            <FaCalendarAlt />

            <span>Cases by date</span>
          </div>
          <DatePicker
            className="px-2   w-[240px] rounded bg-secondary"
            selectsRange={true}
            showYearDropdown
            startDate={startDate}
            endDate={endDate}
            onChange={(update) => {
              setDateRange(update);
            }}
            dateFormat="d/M/yyyy"
            isClearable={true}
          />
        </div>
      </div>

      {/* {showAdd && (
        <div className=" flex justify-between items-center bg-sidebar px-5 py-2 rounded my-2">
          <input
            onChange={(e) => {
              setName(e.target.value);
            }}
            value={name}
            className="text-slate-300  w-1/2 bg-slate-600 px-2 py-1 rounded  outline-none focus:ring-1 ring-primary  "
            placeholder="Type name"
          />
          <div className="flex space-x-2">
            <button
              onClick={handleAdd}
              className="bg-primary/70 hover:bg-primary text-white px-3 py-2 rounded"
            >
              <MdSave className="w-5 h-5" />
            </button>
            <button
              onClick={() => setShowAdd(false)}
              className="bg-red-500/70 px-3 py-2 text-white hover:bg-red-500 rounded"
            >
              <MdCancel className="w-5 h-5" />
            </button>
          </div>
        </div>
      )} */}

      <div className="whitespace-nowrap px-5 self-center">
        <strong className="text-primary">{rows.length}</strong> Records
      </div>
      <div className="bg-secondary/80  rounded-lg text-xs  ">
        <table {...getTableProps()} className=" w-full  overflow-hidden       ">
          <thead className="h-[70px] absolute    border-b-2  border-primary  invisible  md:visible md:static   ">
            {headerGroups.map((headerGroup, i) => (
              <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, i) => (
                  <th
                    className="pl-5 text-left"
                    key={i}
                    {...column.getHeaderProps()}
                  >
                    <span {...column.getSortByToggleProps()}>
                      {column.render("Header")}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          {!loading && (
            <tbody className="bg-gray-700/90" {...getTableBodyProps()}>
              {page.map((row, i) => {
                prepareRow(row);
                return (
                  <tr
                    onClick={() => {
                      // console.log(row.original);
                    }}
                    className="flex flex-col justify-center cursor-pointer  md:h-[50px] h-full py-5   md:text-left  md:table-row hover:bg-slate-700     border-b border-slate-900/50  "
                    key={i}
                    {...row.getRowProps()}
                  >
                    {row.cells.map((cell, i) => {
                      return (
                        <td
                          data-heading={cell.column.Header}
                          className={`   flex md:table-cell before:px-3 before:md:absolute before:md:invisible md:pl-5   before:relative before:w-[100px]      before:text-slate-300  before:content-[attr(data-heading)]    `}
                          key={i}
                          {...cell.getCellProps()}
                        >
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                    <td className=" pt-5 md:pt-0  sm:w-1/3  text-center align-middle flex md:table-cell flex-col w-1/2 self-center space-y-2 md:space-y-0 md:space-x-2  md:w-fit ">
                      <div className="flex items-center justify-end space-x-2 px-4">
                        <button
                          className="flex md:table-cell justify-center  items-center  transition-all hover:bg-blue-500 text-blue-500 hover:text-slate-200 px-3 py-2  rounded "
                          onClick={() => {
                            handleNavigate(row.original);
                          }}
                        >
                          <AiFillEye className=" w-4 h-4" />
                        </button>

                        <button
                          className="flex md:table-cell justify-center  items-center  transition-all hover:bg-orange-400/70 text-orange-400 hover:text-slate-200 px-3 py-2  rounded "
                          onClick={() => {
                            handleNavigate(row.original, true);
                          }}
                        >
                          <AiFillEdit className=" w-4 h-4 " />
                        </button>
                        <button
                          className="flex md:table-cell justify-center  items-center  transition-all hover:bg-red-500/80 text-red-500/80 hover:text-slate-200 px-3 py-2  rounded "
                          onClick={() => {
                            handleDelete(row.original.id);
                          }}
                        >
                          <AiFillDelete className=" w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          )}
        </table>
        <div className="pagination flex justify-center items-center space-x-3 py-2">
          <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
            {"<<"}
          </button>{" "}
          <button onClick={() => previousPage()} disabled={!canPreviousPage}>
            {"<"}
          </button>{" "}
          <button onClick={() => nextPage()} disabled={!canNextPage}>
            {">"}
          </button>{" "}
          <button
            onClick={() => gotoPage(pageCount - 1)}
            disabled={!canNextPage}
          >
            {">>"}
          </button>{" "}
          <span>
            Page{" "}
            <strong>
              <span className="text-primary">{pageIndex + 1}</span> of{" "}
              {pageOptions.length}
            </strong>{" "}
          </span>
          <span>
            | Go to page:{" "}
            <input
              className=" rounded bg-sidebar  px-1"
              type="number"
              defaultValue={pageIndex + 1}
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                gotoPage(page);
              }}
              style={{ width: "100px" }}
            />
          </span>{" "}
          <select
            className="bg-secondary/70"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
            }}
          >
            {[10, 20, 30, 40, 50, data.length].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize > 50 ? "All" : pageSize}
              </option>
            ))}
          </select>
        </div>
        {loading && (
          <div className="flex w-full h-full justify-center items-center">
            <Oval color="royalblue" secondaryColor="white" />
          </div>
        )}
      </div>
    </>
  );
};

export default CaseLogTable;
